import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { Square2StackIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { SiFacebook, SiPinterest, SiX } from "@icons-pack/react-simple-icons";
import {
    Dispatch,
    ElementType,
    ReactNode,
    SetStateAction,
    useState,
} from "react";
import { FormattedMessage } from "react-intl";
import { m, AnimatePresence, LazyMotion, domAnimation } from "motion/react";
import LinkedinIcon from "../icons/linkedin";

interface SocialLinkInterface {
    name: string;
    Icon: ElementType;
    iconClass?: string;
    backgroundClass?: string;
    href: string;
    postShare: (shareMethod: string) => void;
}

function SocialLink({
    name,
    Icon,
    iconClass,
    backgroundClass,
    href,
    postShare,
}: SocialLinkInterface) {
    return (
        <a
            className="flex justify-center flex-col items-center group"
            href={href}
            target="_blank"
            rel="noreferrer"
            onClick={() => postShare(name)}
        >
            <div
                className={`rounded-full ${backgroundClass} size-16 flex justify-center items-center`}
            >
                <Icon className={`size-6 ${iconClass}`} />
            </div>
            <span className="text-sm text-center text-gray-700 pt-2">
                {name}
            </span>
        </a>
    );
}

interface ShareUrlProps {
    url: string;
    trigger: (e: Dispatch<SetStateAction<boolean>>) => ReactNode;
    postShare?: (shareMethod?: string) => void;
}

export default function ShareUrl({
    url,
    trigger,
    postShare = () => null,
}: ShareUrlProps) {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const encodedUrl = encodeURIComponent(url);
    const urls = {
        pinterest: `http://pinterest.com/pin/create/link/?url=${encodedUrl}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
        linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}`,
        twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}`,
    };

    const close = () => {
        setOpen(false);
        setCopied(false);
    };

    const copyConfirm = () => {
        postShare("clipboard");
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <LazyMotion features={domAnimation}>
            {trigger(setOpen)}
            <Dialog
                open={open}
                onClose={close}
                className="relative z-999"
                key="foo"
            >
                <div
                    className="fixed inset-0 bg-gray-500/75"
                    aria-hidden="true"
                />
                <m.div
                    className="fixed inset-0 flex w-screen items-end sm:items-center justify-center p-2 sm:p-8"
                    initial={{ y: 500 }}
                    animate={{ y: 0 }}
                >
                    <DialogPanel className="w-full sm:max-w-lg rounded-xl bg-white p-8">
                        <div className="flex items-center">
                            <DialogTitle className="text-2xl leading-6 text-gray-900 font-medium">
                                <FormattedMessage id="Share" />
                            </DialogTitle>
                            <button
                                onClick={close}
                                className="rounded-md bg-white text-gray-400 hover:text-gray-500 ml-auto"
                            >
                                <span className="sr-only">
                                    <FormattedMessage id="Close" />
                                </span>
                                <XMarkIcon className="size-6" />
                            </button>
                        </div>
                        <div className="flex flex-col gap-y-5">
                            <div className="grid grid-cols-4 pt-4">
                                <SocialLink
                                    name="Twitter"
                                    Icon={SiX}
                                    iconClass="text-black"
                                    backgroundClass="bg-black/5 group-hover:bg-black/15"
                                    href={urls.twitter}
                                    postShare={postShare}
                                />
                                <SocialLink
                                    name="Linkedin"
                                    Icon={LinkedinIcon}
                                    iconClass="text-linkedin"
                                    backgroundClass="bg-linkedin/5 group-hover:bg-linkedin/15"
                                    href={urls.linkedin}
                                    postShare={postShare}
                                />
                                <SocialLink
                                    name="Facebook"
                                    Icon={SiFacebook}
                                    iconClass="text-facebook"
                                    backgroundClass="bg-facebook/5 group-hover:bg-facebook/15"
                                    href={urls.facebook}
                                    postShare={postShare}
                                />
                                <SocialLink
                                    name="Pinterest"
                                    Icon={SiPinterest}
                                    iconClass="text-pinterest"
                                    backgroundClass="bg-pinterest/5 group-hover:bg-pinterest/15"
                                    href={urls.pinterest}
                                    postShare={postShare}
                                />
                            </div>
                            <span className="text-gray-700 font-bold">
                                <FormattedMessage id="Or copy link" />
                            </span>
                            <div>
                                <div className="bg-gray-100 text-gray-400 py-4 rounded-lg px-4 flex justify-between">
                                    <AnimatePresence mode="wait">
                                        {copied ? (
                                            <m.div
                                                className="flex justify-between w-full"
                                                key="copied"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                transition={{
                                                    duration: 0.3,
                                                }}
                                            >
                                                <span className="shrink whitespace-nowrap text-ellipsis overflow-hidden">
                                                    <span className="text-green-500">
                                                        <FormattedMessage id="Link copied!" />
                                                    </span>
                                                </span>
                                                <div className="pl-1">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        strokeWidth={1.5}
                                                        stroke="currentColor"
                                                        className="text-green-500 size-5 shrink-0 grow"
                                                    >
                                                        <m.path
                                                            initial={{
                                                                pathLength: 0,
                                                            }}
                                                            animate={{
                                                                pathLength: 1,
                                                            }}
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M4.5 12.75l6 6 9-13.5"
                                                        />
                                                    </svg>
                                                </div>
                                            </m.div>
                                        ) : (
                                            <m.div
                                                className="flex justify-between w-full"
                                                key="link"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                transition={{
                                                    duration: 0.3,
                                                }}
                                            >
                                                <span className="shrink whitespace-nowrap text-ellipsis overflow-hidden">
                                                    <span>{url}</span>
                                                </span>
                                                <button
                                                    onClick={() =>
                                                        navigator.clipboard
                                                            .writeText(url)
                                                            .then(copyConfirm)
                                                    }
                                                    className="pl-1"
                                                >
                                                    <Square2StackIcon className="text-black size-5 shrink-0 grow" />
                                                </button>
                                            </m.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </div>
                        </div>
                    </DialogPanel>
                </m.div>
            </Dialog>
        </LazyMotion>
    );
}
